import React, { useState, useEffect, useCallback } from 'react';
import axios from "axios";
import { toast } from 'react-toastify';
import Select from "../../components/select";
import { CFormSwitch } from "@coreui/react";

import styles from './SummarySection.module.scss';
import Spinner from '../../components/spinner';
import Button from '../../components/button';
import DayEditPopup from '../../components/dayEditPopup';
import { SELECT_COMPONENTS } from './summaryOption';
import { useLanguage } from '../../context/languageContext';
import { fetchAllSummaryOptions } from '../../helper';
import EditPageHeader, { EditPageLayout } from "../../components/editPageHeader";
import WeekSchedule from "../../components/weekSchedule";
import { useNavigate } from "react-router-dom";
import { titleMissing, TITLE_HINT } from "../../helper/sectionTitles";

const API = process.env.REACT_APP_API_ROOT;

/**
 * Controls the mobile Summary tab's "Today's Featured Summary" card: its title
 * per language, the book it shows, and whether it appears. Each day of the
 * week strip opens that day in the day editor (`components/dayEditPopup`).
 *
 * There is no colour setting: the colour behind the book is the cover's own,
 * served by the backend.
 */
function SummarySectionPage() {
    const { adminInfo, selectedLang, languages, languageLabels } = useLanguage();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    const [section, setSection] = useState(null);
    const [summaries, setSummaries] = useState([]);
    const [titleLang, setTitleLang] = useState(selectedLang || "en");
    // The date open in the day editor, or null.
    const [editingDay, setEditingDay] = useState(null);
    // Bumped when the day editor saved something, so the week view refetches.
    const [scheduleVersion, setScheduleVersion] = useState(0);
    const [loadFailed, setLoadFailed] = useState(false);
    // The section as last saved. The week strip and the day editor show an
    // unscheduled day with THIS — it is what the app serves — not with the
    // book still being edited above.
    const [saved, setSaved] = useState(null);

    const fetchSection = useCallback(async () => {
        setLoading(true);
        setLoadFailed(false);
        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.get(`${API}/api/admin/summary/section`, { headers });

            if (response.status && response?.data?.sectionId) {
                setSection(response.data);
                setSaved(response.data);
            } else {
                setLoadFailed(true);
                toast.error(response.message || "Could not load the featured summary.");
            }
        } catch (error) {
            console.error("fetchSection catch error::", error);
            setLoadFailed(true);
            toast.error("Could not load the featured summary.");
        } finally {
            setLoading(false);
        }
    }, [adminInfo]);

    // The section payload carries every language at once, so it must NOT refetch
    // when the editing language changes — doing so wiped unsaved title and book
    // edits. Only the picker options are language-scoped.
    useEffect(() => {
        if (adminInfo?.token) fetchSection();
    }, [adminInfo, fetchSection]);

    useEffect(() => {
        if (!adminInfo?.token) return;
        fetchAllSummaryOptions(selectedLang, adminInfo.token).then((options) =>
            setSummaries(options.map((o) => ({
                label: o.bookName,
                value: o.summaryId,
                image: o.image,
            })))
        );
    }, [adminInfo, selectedLang]);

    const titleForLang = (langCode) =>
        (section?.translations || []).find((t) => t.langCode === langCode)?.title || "";

    const handleChangeTitle = (langCode, title) => {
        setSection((prev) => {
            const translations = [...(prev.translations || [])];
            const index = translations.findIndex((t) => t.langCode === langCode);
            if (index >= 0) translations[index] = { ...translations[index], title };
            else translations.push({ langCode, title });
            return { ...prev, translations };
        });
    };

    const handleToggleActive = async (isActive) => {
        setSection((prev) => ({ ...prev, isActive }));
        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.put(
                `${API}/api/admin/summary/section/update/${section.sectionId}`,
                { values: { isActive } },
                { headers }
            );
            if (response.status) {
                toast.success(isActive ? "Featured summary is now visible." : "Featured summary is now hidden.");
            } else {
                setSection((prev) => ({ ...prev, isActive: !isActive }));
                toast.error(response.message || "Could not update visibility.");
            }
        } catch (error) {
            console.error("handleToggleActive catch error::", error);
            setSection((prev) => ({ ...prev, isActive: !isActive }));
            toast.error("Could not update visibility.");
        }
    };

    const handleSave = async () => {
        if (!titleForLang("en").trim()) {
            toast.error("An English title is required — it's the fallback for every language.");
            return;
        }
        if (!(section.items || []).length) {
            toast.error("Choose a book to feature.");
            return;
        }
        try {
            setIsSubmit(true);
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.put(
                `${API}/api/admin/summary/section/update/${section.sectionId}`,
                {
                    values: {
                        translations: (section.translations || []).filter((t) => t.title && t.title.trim()),
                        items: (section.items || []).map((i) => ({ summaryId: i.summaryId })),
                    },
                },
                { headers }
            );

            if (response.status) {
                toast.success("Featured summary has been updated.");
                fetchSection();
            } else {
                toast.error(response.message || "Could not save.");
            }
        } catch (error) {
            console.error("handleSave catch error::", error);
            toast.error(error.response?.data?.error || "Could not save.");
        } finally {
            setIsSubmit(false);
        }
    };

    const selectedBook = (section?.items || [])[0];
    const savedBook = (saved?.items || [])[0];
    const savedDefault = savedBook
        ? {
            bookName: savedBook.bookName,
            image: savedBook.image || summaries.find((o) => o.value === savedBook.summaryId)?.image || "",
        }
        : null;

    return (
        <EditPageLayout>
            <EditPageHeader
                title="Edit Summary Section"
                subtitle={titleForLang("en") || "Today's Featured Summary"}
                note="The featured card on the app's Summary tab. Set a default book here, or schedule a different one for each day. The colour behind the book comes from its cover."
                actions={
                    <button type="button" className="btn btn-outline-secondary" onClick={() => navigate("/summary-sections")}>
                        Back
                    </button>
                }
            />

            {loadFailed && !loading ? (
                // Without this the page span forever on a failed load, with the
                // toast long gone and no way to retry.
                <div className={styles.container}>
                    <div className={styles.panel}>
                        <p className="mb-3">Couldn't load the featured summary.</p>
                        <button type="button" className="btn btn-dark" onClick={fetchSection}>
                            Retry
                        </button>
                    </div>
                </div>
            ) : loading || !section ? (
                <Spinner />
            ) : (
                <div className={styles.container}>
                    <div className={styles.panel}>
                        <div className="mb-3 d-flex align-items-center justify-content-between">
                            <label className="label mb-0">Show this card in the app</label>
                            <CFormSwitch
                                size="lg"
                                variant="3d"
                                color="primary"
                                style={{ paddingLeft: 0, width: "42px", height: "22px", cursor: "pointer" }}
                                checked={section.isActive === true}
                                onChange={(e) => handleToggleActive(e.target.checked)}
                            />
                        </div>

                        <div className="mb-3">
                            <label className="label">Title:</label>
                            <div className="d-flex flex-wrap gap-1 mb-2">
                                {(languages || []).map((lang) => (
                                    <button
                                        key={lang}
                                        type="button"
                                        className={`btn btn-sm ${titleLang === lang ? "btn-dark" : "btn-outline-secondary"}`}
                                        onClick={() => setTitleLang(lang)}
                                    >
                                        {languageLabels?.[lang] || lang}{titleMissing(lang, titleForLang(lang), titleForLang("en")) ? " •" : ""}
                                    </button>
                                ))}
                            </div>
                            <input
                                className="form_control"
                                type="text"
                                placeholder={`Title in ${languageLabels?.[titleLang] || titleLang}...`}
                                value={titleForLang(titleLang)}
                                onChange={(e) => handleChangeTitle(titleLang, e.target.value)}
                            />
                            <small className="text-muted">
                                {TITLE_HINT}
                            </small>
                        </div>

                        <div className="mb-3">
                            <label className="label">Default book:</label>
                            <Select
                                className="form-control-md fw-medium p-0"
                                isClearable
                                placeholder="Choose a summary..."
                                value={selectedBook ? { label: selectedBook.bookName, value: selectedBook.summaryId } : null}
                                onChange={(selected) => {
                                    setSection((prev) => ({
                                        ...prev,
                                        items: selected
                                            ? [{ summaryId: selected.value, bookName: selected.label, image: selected.image }]
                                            : [],
                                    }));
                                }}
                                options={summaries}
                                components={SELECT_COMPONENTS}
                            />
                            <small className="text-muted">
                                Shown on any day that has nothing scheduled.
                            </small>
                        </div>

                        <div className="d-flex gap-2 justify-content-end">
                            <button type="button" className="btn btn-outline-secondary" disabled={isSubmit} onClick={() => navigate("/summary-sections")}>
                                Cancel
                            </button>
                            <Button onClick={handleSave} disabled={isSubmit} isSubmit={isSubmit} label="Saving ..." text="Save" />
                        </div>
                    </div>

                    {/* Which book the card shows each day, at a glance. Days
                        left empty show the default book above, dimmed. */}
                    <div className={styles.schedulePanel}>
                        <WeekSchedule
                            endpoint={`${API}/api/admin/summary/section/${section.sectionId}/schedule`}
                            token={adminInfo?.token}
                            toItem={(item) => ({ id: item.summaryId, name: item.bookName, image: item.image })}
                            fallback={savedDefault ? { name: savedDefault.bookName, image: savedDefault.image } : null}
                            shape="cover"
                            reloadKey={scheduleVersion}
                            onEdit={() => setEditingDay(new Date().toLocaleDateString("en-CA"))}
                            onSelectDay={setEditingDay}
                        />
                    </div>
                </div>
            )}

            {editingDay && section && (
                <DayEditPopup
                    key={section.sectionId}
                    endpoint={`${API}/api/admin/summary/section/${section.sectionId}/schedule`}
                    initialDate={editingDay}
                    options={summaries}
                    selectComponents={SELECT_COMPONENTS}
                    toOption={(item) => ({ value: item.summaryId, label: item.bookName, image: item.image })}
                    // accentColor null: the card's colour is the cover's, and a
                    // stale per-day colour left on the row would only mislead.
                    toEntry={(option) => ({ summaryIds: option ? [option.value] : [], accentColor: null })}
                    defaultName={savedDefault?.bookName}
                    label="Book:"
                    noun="book"
                    onClose={(changed) => {
                        setEditingDay(null);
                        if (changed) setScheduleVersion((v) => v + 1);
                    }}
                />
            )}
        </EditPageLayout>
    );
}

export default SummarySectionPage;
